import { type Entity } from '@xeokit/xeokit-sdk';
import { shallowRef, type ShallowRef } from 'vue';
import { getEntityIdsByType } from '~/utils/viewer/metadata-graph';
import { WalkMode, type WalkModeTarget } from '~/utils/viewer/tools/walk-mode';
export type ToolbarStoreState = {
  threeDMode: boolean;
  perspectiveOrthoMode: { active: boolean; enabled: boolean };
  firstPersonNavigationMode: { active: boolean; enabled: boolean };
  walkMode: { active: boolean; enabled: boolean };
  ifcSpacesVisible: { active: boolean; enabled: boolean };
  hideObjects: { active: boolean; enabled: boolean };
  selectObjects: { active: boolean; enabled: boolean };
  marqueeSelectObjects: { active: boolean; enabled: boolean };
  measureAngleMode: { active: boolean; enabled: boolean };
  measureDistanceMode: { active: boolean; enabled: boolean };
  sliceObjectsMode: { active: boolean; enabled: boolean; actionActive: boolean };
  zoneFeatureMode: { active: boolean; enabled: boolean };
  annotationsMode: { active: boolean; enabled: boolean; visible: boolean; actionActive: boolean };
  minimapActive: boolean;
};

export const useToolbarStore = defineStore('toolbar-store', () => {
  const { $math, $os } = useNuxtApp();
  const viewerStore = useViewerStore();
  const viewerDistanceMeasurementsStore = useViewerDistanceMeasurementsStore();
  const viewerSectionsStore = useViewerSectionsStore();
  const viewerZonesStore = useViewerZonesStore();
  const projectStore = useProjectStore();

  const tempVec3a = $math.vec3();

  const state: ToolbarStoreState = reactive({
    threeDMode: true,
    perspectiveOrthoMode: { active: true, enabled: true },
    firstPersonNavigationMode: { active: false, enabled: true },
    walkMode: { active: false, enabled: true },
    ifcSpacesVisible: { active: false, enabled: true },
    hideObjects: { active: false, enabled: true },
    selectObjects: { active: false, enabled: true },
    marqueeSelectObjects: { active: false, enabled: true },
    measureDistanceMode: { active: false, enabled: true },
    measureAngleMode: { active: false, enabled: true },
    sliceObjectsMode: { active: false, enabled: true, actionActive: false },
    zoneFeatureMode: { active: false, enabled: true },
    annotationsMode: { active: false, enabled: true, visible: false, actionActive: false },
    minimapActive: false,
  });

  let onPickEvent: string | undefined = undefined;
  let onNothingPickEvent: string | undefined = undefined;
  let onHoverEvent: string | undefined = undefined;
  let onMouseMoveEvent: string | undefined = undefined;
  let onSectionsMouseMoveEvent: string | undefined;
  let sectionsHelperOnSceneTick: string | undefined;

  let walkModeController: WalkMode | undefined = undefined;
  const walkModeTarget: Ref<WalkModeTarget> = ref(null);
  const walkModeHideMode: Ref<boolean> = ref(false);
  const walkModeFlying: Ref<boolean> = ref(false);
  const walkModeMoveMode: Ref<boolean> = ref(false);
  const walkModeSnap: Ref<boolean> = ref(true);
  const walkModeMoveEnabled: Ref<boolean> = ref(true);
  const walkModeInspect: Ref<boolean> = ref(false);
  const walkModePressedKeys: Ref<string[]> = ref([]);

  let lastHighlightedEntity: Entity | null = null;
  const lastSelectedEntity: ShallowRef<Entity | null> = shallowRef(null);

  const controlPressed: Ref<boolean> = ref(false);

  function setSelectObjects(active: boolean) {
    const keyListenerAbortController = new AbortController();
    const viewer = viewerStore.getHelpers().viewer;

    if (!viewer) return;

    state.selectObjects.active = active;

    if (state.selectObjects.active) {
      state.marqueeSelectObjects.enabled = false;
      state.hideObjects.enabled = false;
      state.measureDistanceMode.enabled = false;
      state.sliceObjectsMode.enabled = false;
      state.annotationsMode.enabled = false;

      setModelsEdgesVisible(true);

      enableHighlightOnHover(viewer);

      window.addEventListener(
        'keydown',
        (event) => {
          if (viewer && (event.key === 'Control' || event.key === 'Meta')) {
            controlPressed.value = true;
          }
        },
        { signal: keyListenerAbortController.signal },
      );

      window.addEventListener(
        'keyup',
        (event) => {
          if (viewer && (event.key === 'Control' || event.key === 'Meta')) {
            controlPressed.value = false;
          }
        },
        { signal: keyListenerAbortController.signal },
      );

      if (!onPickEvent) {
        onPickEvent = viewer.cameraControl.on('picked', handleCameraControlPicked);
      }

      if (!onNothingPickEvent) {
        onNothingPickEvent = viewer.cameraControl.on(
          'pickedNothing',
          handleCameraControlPickedNothing,
        );
      }
    } else {
      keyListenerAbortController.abort();

      disableHighlightOnHover(viewer);

      state.marqueeSelectObjects.enabled = true;
      state.hideObjects.enabled = true;
      state.measureDistanceMode.enabled = true;
      state.sliceObjectsMode.enabled = true;
      state.annotationsMode.enabled = true;

      if (projectStore.project) {
        setModelsEdgesVisible(projectStore.project.showEdges);
      } else {
        setModelsEdgesVisible(false);
      }
    }
  }

  function enableHighlightOnHover(viewer) {
    viewer.scene.highlightMaterial.fill = true;
    viewer.scene.highlightMaterial.edges = true;
    viewer.scene.highlightMaterial.fillAlpha = 0.1;
    viewer.scene.highlightMaterial.edgeAlpha = 1;
    viewer.scene.highlightMaterial.edgeColor = [1, 1, 0];

    if (!onHoverEvent) {
      onHoverEvent = viewer.cameraControl.on('hover', handleCameraControlHover);
    }

    if (!onMouseMoveEvent) {
      // @ts-ignore xeokit
      onMouseMoveEvent = viewer.scene.input.on('mousemove', handleSceneInputMouseMove);
    }
  }

  function disableHighlightOnHover(viewer) {
    if (onMouseMoveEvent !== undefined) {
      viewer.scene.input.off(onMouseMoveEvent);
      onMouseMoveEvent = undefined;
    }

    if (onHoverEvent !== undefined) {
      viewer.cameraControl.off(onHoverEvent);
      onHoverEvent = undefined;
    }

    if (lastHighlightedEntity) lastHighlightedEntity.highlighted = false;
  }

  function setHideObjects(active: boolean) {
    const viewer = viewerStore.getHelpers().viewer;
    if (!viewer) return;

    state.hideObjects.active = active;

    if (state.hideObjects.active) {
      state.marqueeSelectObjects.active = false;
      state.marqueeSelectObjects.enabled = false;
      state.selectObjects.enabled = false;
      state.measureDistanceMode.enabled = false;
      state.sliceObjectsMode.enabled = false;
      state.annotationsMode.enabled = false;

      setModelsEdgesVisible(true);

      enableHighlightOnHover(viewer);

      if (!onPickEvent) {
        onPickEvent = viewer.cameraControl.on('picked', handleCameraControlPicked);
      }
    } else {
      if (onHoverEvent !== undefined) {
        viewer.cameraControl.off(onHoverEvent);
        onHoverEvent = undefined;
      }

      disableHighlightOnHover(viewer);

      state.marqueeSelectObjects.enabled = true;
      state.selectObjects.enabled = true;
      state.measureDistanceMode.enabled = true;
      state.sliceObjectsMode.enabled = true;
      state.annotationsMode.enabled = true;

      if (projectStore.project) {
        setModelsEdgesVisible(projectStore.project.showEdges);
      } else {
        setModelsEdgesVisible(false);
      }
    }
  }

  watch(state.sliceObjectsMode, () => {
    const viewer = viewerStore.getHelpers().viewer;
    if (!viewer) return;
    if (
      state.sliceObjectsMode.actionActive &&
      state.sliceObjectsMode.enabled &&
      state.sliceObjectsMode.active
    ) {
      onSectionsMouseMoveEvent = viewer.scene.input.on(
        //@ts-ignore
        'mousemove',
        viewerSectionsStore.handleMouseMove,
      );

      sectionsHelperOnSceneTick = viewerSectionsStore.helperOnSceneTick(viewer);
    } else if (
      !state.sliceObjectsMode.actionActive ||
      !state.sliceObjectsMode.active ||
      !state.sliceObjectsMode.enabled
    ) {
      viewerSectionsStore.hideHelper();
      if (onSectionsMouseMoveEvent !== undefined) {
        viewer.scene.input.off(onSectionsMouseMoveEvent);
      }
      if (sectionsHelperOnSceneTick !== undefined) {
        viewer.scene.off(sectionsHelperOnSceneTick);
      }
      sectionsHelperOnSceneTick = undefined;
      onSectionsMouseMoveEvent = undefined;
    }
  });

  function setSliceObjects(active: boolean) {
    state.sliceObjectsMode.active = active;
    const sectionStore = useViewerSectionsStore();

    if (state.sliceObjectsMode.active) {
      if (sectionStore.numberOfSections === 0) {
        state.sliceObjectsMode.actionActive = true;
      }

      state.marqueeSelectObjects.enabled = false;
      state.selectObjects.enabled = false;
      state.measureDistanceMode.enabled = false;
      state.measureAngleMode.enabled = false;
      state.zoneFeatureMode.enabled = false;
      state.annotationsMode.enabled = false;
      state.hideObjects.enabled = false;
      state.hideObjects.active = false;
    } else {
      viewerSectionsStore.hideControl();
      viewerSectionsStore.editedSectionPlaneId = undefined;
      state.marqueeSelectObjects.enabled = true;
      state.sliceObjectsMode.actionActive = false;
      state.selectObjects.enabled = true;
      state.measureDistanceMode.enabled = true;
      state.measureAngleMode.enabled = true;
      state.zoneFeatureMode.enabled = true;
      state.annotationsMode.enabled = true;
      state.hideObjects.enabled = true;
    }
  }

  function setMeasureAngle(active: boolean) {
    state.measureAngleMode.active = active;

    if (state.measureAngleMode.active) {
      state.measureDistanceMode.enabled = false;
      state.sliceObjectsMode.enabled = false;
      state.zoneFeatureMode.enabled = false;
      state.annotationsMode.enabled = false;
      state.hideObjects.enabled = false;
      state.selectObjects.enabled = false;
      state.marqueeSelectObjects.enabled = false;
    } else {
      state.measureDistanceMode.enabled = true;
      state.sliceObjectsMode.enabled = true;
      state.zoneFeatureMode.enabled = true;
      state.annotationsMode.enabled = true;
      state.hideObjects.enabled = true;
      state.selectObjects.enabled = true;
      state.marqueeSelectObjects.enabled = true;
    }
  }

  function setMeasureDistance(active: boolean) {
    state.measureDistanceMode.active = active;

    const distanceMeasurementMouseControl = viewerDistanceMeasurementsStore.getMouseControl();
    const distanceMeasurementTouchControl = viewerDistanceMeasurementsStore.getTouchControl();

    if (!distanceMeasurementMouseControl || !distanceMeasurementTouchControl) return;

    const viewer = viewerStore.getHelpers().viewer;
    if (!viewer) return;

    //@ts-ignore
    const viewerCanvas = viewer.scene.canvas.canvas as HTMLCanvasElement;

    const handleMouseControl = () => {
      if (!state.measureDistanceMode.active) return;
      distanceMeasurementMouseControl.activate();
      viewerCanvas.removeEventListener('mousemove', handleMouseControl);
    };

    if (state.measureDistanceMode.active) {
      state.measureAngleMode.enabled = false;
      state.sliceObjectsMode.enabled = false;
      state.zoneFeatureMode.enabled = false;
      state.annotationsMode.enabled = false;
      state.hideObjects.enabled = false;
      state.selectObjects.enabled = false;
      state.marqueeSelectObjects.enabled = false;

      //viewer.cameraControl.panRightClick = false;

      if ($os.isTouchDevice()) {
        distanceMeasurementTouchControl.activate();
      }

      viewerCanvas.addEventListener('mousemove', handleMouseControl);
    } else {
      state.measureAngleMode.enabled = true;
      state.sliceObjectsMode.enabled = true;
      state.zoneFeatureMode.enabled = true;
      state.annotationsMode.enabled = true;
      state.hideObjects.enabled = true;
      state.selectObjects.enabled = true;
      state.marqueeSelectObjects.enabled = true;

      //viewer.cameraControl.panRightClick = true;

      distanceMeasurementMouseControl.deactivate();
      distanceMeasurementTouchControl.deactivate();
    }
  }

  function setShowIfcSpaces(active: boolean) {
    const viewer = viewerStore.getHelpers().viewer;
    if (!viewer) return;

    state.ifcSpacesVisible.active = active;

    const objectIds = getEntityIdsByType(viewer, 'IfcSpace');

    viewer.scene.setObjectsCulled(objectIds, !active);
  }

  function setOrthoMode(active: boolean, done = () => {}) {
    if (!state.perspectiveOrthoMode.enabled) return;

    const viewer = viewerStore.getHelpers().viewer;
    if (!viewer) return;

    state.perspectiveOrthoMode.active = active;

    if (state.perspectiveOrthoMode.active) {
      if (done) {
        viewer.cameraFlight.flyTo({ projection: 'perspective', duration: 0.5 }, done);
      } else {
        viewer.cameraFlight.jumpTo({ projection: 'perspective' });
      }
    } else {
      if (done) {
        viewer.cameraFlight.flyTo({ projection: 'ortho', duration: 0.5 }, done);
      } else {
        viewer.cameraFlight.jumpTo({ projection: 'ortho' });
      }
    }
  }

  function setThreeDMode(active: boolean) {
    state.threeDMode = active;

    if (state.threeDMode) {
      _enterThreeDMode(() => {});
    } else {
      _exitThreeDMode(() => {});
    }

    function _enterThreeDMode(done?: () => void) {
      const viewer = viewerStore.getHelpers().viewer;
      if (!viewer) return;

      const scene = viewer.scene;
      const aabb = scene.getAABB(scene.visibleObjectIds);
      const diag = $math.getAABB3Diag(aabb);
      const center = $math.getAABB3Center(aabb, () => tempVec3a);
      const dist = Math.abs(diag / Math.tan(65.0 / 2)); // TODO: fovy match with CameraFlight
      const camera = scene.camera;

      const dir = camera.yUp ? [-1, -1, -1] : [1, 1, 1];
      const up = camera.yUp ? [-1, 1, -1] : [-1, 1, 1];

      viewer.cameraControl.pivotPos = center;

      const navCubePlugin = viewerStore.getHelpers().navCubePlugin;
      navCubePlugin!.setVisible(true);

      state.firstPersonNavigationMode.enabled = true;
      state.walkMode.enabled = true;
      state.sliceObjectsMode.enabled = true;
      state.perspectiveOrthoMode.enabled = true;

      setThreeDModeActive(true);

      if (done) {
        viewer.cameraFlight.flyTo(
          {
            look: center,
            eye: [center[0] - dist * dir[0], center[1] - dist * dir[1], center[2] - dist * dir[2]],
            up: up,
            orthoScale: diag * 1.3,
            duration: 1,
            projection: state.perspectiveOrthoMode.active ? 'ortho' : 'perspective',
          },
          () => {
            done();
          },
        );
      } else {
        viewer.cameraFlight.jumpTo({
          look: center,
          eye: [center[0] - dist * dir[0], center[1] - dist * dir[1], center[2] - dist * dir[2]],
          up: up,
          // @ts-ignore
          orthoScale: diag * 1.3,
          projection: state.perspectiveOrthoMode.active ? 'ortho' : 'perspective',
        });
      }
    }

    function _exitThreeDMode(done?: () => void) {
      const viewer = viewerStore.getHelpers().viewer;
      if (!viewer) return;

      const scene = viewer.scene;
      const camera = scene.camera;
      const aabb = scene.getAABB(scene.visibleObjectIds);
      const look2 = $math.getAABB3Center(aabb);
      const diag = $math.getAABB3Diag(aabb);
      const fitFOV = 45; // fitFOV;
      const sca = Math.abs(diag / Math.tan(fitFOV * $math.DEGTORAD));
      const orthoScale2 = diag * 1.3;
      const eye2 = tempVec3a;

      eye2[0] = look2[0] + camera.worldUp[0] * sca;
      eye2[1] = look2[1] + camera.worldUp[1] * sca;
      eye2[2] = look2[2] + camera.worldUp[2] * sca;

      const up2 = $math.mulVec3Scalar(camera.worldForward, -1, []);

      state.sliceObjectsMode.active = false;

      state.firstPersonNavigationMode.enabled = false;
      state.firstPersonNavigationMode.active = false;

      setWalkMode(false);
      state.walkMode.enabled = false;

      state.perspectiveOrthoMode.enabled = false;
      state.perspectiveOrthoMode.active = false;

      setThreeDModeActive(false);

      if (done) {
        viewer.cameraFlight.flyTo(
          {
            eye: eye2,
            look: look2,
            up: up2,
            orthoScale: orthoScale2,
            projection: 'ortho',
          },
          () => {
            const navCubePlugin = viewerStore.getHelpers().navCubePlugin;
            navCubePlugin?.setVisible(false);
          },
        );
      } else {
        viewer.cameraFlight.jumpTo({
          eye: eye2,
          look: look2,
          up: up2,
          // @ts-ignore
          orthoScale: orthoScale2,
          projection: 'ortho',
        });

        const navCubePlugin = viewerStore.getHelpers().navCubePlugin;
        navCubePlugin?.setVisible(false);
      }
    }
  }

  function setZonesFeatureMode(active: boolean) {
    console.log('toggleZonesFeatureMode toggle');
    const zoneMouseControl = viewerZonesStore.getMouseControl();
    if (!zoneMouseControl) return;

    state.zoneFeatureMode.active = active;

    if (state.zoneFeatureMode.active) {
      state.measureDistanceMode.enabled = false;
      state.measureAngleMode.enabled = false;
      state.sliceObjectsMode.enabled = false;

      const altitude = 0.01;
      const height = 1.9;
      const color = '#008000';
      const alpha = 0.5;
      zoneMouseControl.activate({ altitude, height, color, alpha });

      //zoneMouseControl.activate();
      console.log('toggleZonesFeatureMode activate');
    } else {
      state.measureDistanceMode.enabled = true;
      state.measureAngleMode.enabled = true;
      state.sliceObjectsMode.enabled = true;
      zoneMouseControl.deactivate();
      console.log('toggleZonesFeatureMode deactivate');
    }
  }

  function setThreeDModeActive(active: boolean) {
    if (active) {
      state.firstPersonNavigationMode.active = false;
      state.marqueeSelectObjects.enabled = true;
      const viewer = viewerStore.getHelpers().viewer;
      viewer!.cameraControl.navMode = 'orbit';
    } else {
      state.marqueeSelectObjects.enabled = false;
      state.marqueeSelectObjects.active = false;
      state.firstPersonNavigationMode.active = false;
      const viewer = viewerStore.getHelpers().viewer;
      viewer!.cameraControl.navMode = 'planView';
    }
  }

  function setFirstPersonModeActive(active: boolean) {
    const viewer = viewerStore.getHelpers().viewer;
    if (!viewer) return;

    state.firstPersonNavigationMode.active = active;

    viewer.cameraControl.navMode = active ? 'firstPerson' : state.threeDMode ? 'orbit' : 'planView';
  }

  function setMarqueeSelect(active: boolean) {
    state.marqueeSelectObjects.active = active;

    if (state.marqueeSelectObjects.active) {
      state.selectObjects.active = false;
      state.selectObjects.enabled = false;
      state.hideObjects.enabled = false;
      state.measureDistanceMode.enabled = false;
      state.sliceObjectsMode.enabled = false;
      state.annotationsMode.enabled = false;
    } else {
      state.selectObjects.enabled = true;
      state.hideObjects.enabled = true;
      state.measureDistanceMode.enabled = true;
      state.sliceObjectsMode.enabled = true;
      state.annotationsMode.enabled = true;
    }
  }

  function setWalkMode(active: boolean) {
    const viewer = viewerStore.getHelpers().viewer;
    if (!viewer) return;

    state.walkMode.active = active;
    walkModeMoveEnabled.value = !viewer.scene.dtxEnabled;

    if (!walkModeController || walkModeController.viewer !== viewer) {
      walkModeController = new WalkMode(viewer, {
        onTarget: (target) => (walkModeTarget.value = target),
        onHideMode: (active) => (walkModeHideMode.value = active),
        onFlying: (active) => (walkModeFlying.value = active),
        onMoveMode: (active) => (walkModeMoveMode.value = active),
        onSnap: (active) => (walkModeSnap.value = active),
        onInspectMode: (active) => {
          const uiStore = useUiStore();

          walkModeInspect.value = active;

          if (active) {
            uiStore.propertiesPanelVisible = true;
            return;
          }

          viewerStore.clearSceneSelections();
          uiStore.propertiesPanelVisible = false;
        },
        onInspectPick: (entityId) => {
          const entity = viewer.scene.objects[entityId];
          if (!entity) return;

          viewerStore.clearSceneSelections();
          entity.selected = true;

          void viewerStore.showObjectProperties(entityId);
        },
        onPressedKeys: (keys) => (walkModePressedKeys.value = keys),
        onToggleMinimap: () => (state.minimapActive = !state.minimapActive),
        onExit: () => setWalkMode(false),
      });
    }

    viewerStore.setFastNavEnabled(!active);

    if (active) {
      if (state.firstPersonNavigationMode.active) setFirstPersonNavigation(false);
      walkModeController.activate();
    } else {
      walkModeController.deactivate();
    }
  }

  function setFirstPersonNavigation(active: boolean) {
    const viewer = viewerStore.getHelpers().viewer;
    if (!viewer) return;

    if (active && state.walkMode.active) setWalkMode(false);

    state.firstPersonNavigationMode.active = active;

    const cameraControl = viewer.cameraControl;
    cameraControl.navMode = 'orbit';

    if (active) {
      cameraControl.followPointer = true;
      // @ts-ignore
      cameraControl.showPivot = false;
      state.marqueeSelectObjects.enabled = true;
      viewer.cameraControl.navMode = 'firstPerson';
    } else {
      if (state.threeDMode) {
        cameraControl.followPointer = true;
        // @ts-ignore
        cameraControl.showPivot = false;
        state.marqueeSelectObjects.enabled = true;
        state.marqueeSelectObjects.active = false;
        viewer.cameraControl.navMode = 'orbit';
      } else {
        // @ts-ignore
        cameraControl.showPivot = true;
        cameraControl.followPointer = false;
        state.marqueeSelectObjects.enabled = false;
        state.marqueeSelectObjects.active = false;
        viewer.cameraControl.navMode = 'planView';
      }
    }
  }

  function setModelsEdgesVisible(edges: boolean) {
    const viewer = viewerStore.getHelpers().viewer;
    if (!viewer) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [key, model] of Object.entries(viewer.scene.models)) {
      (model as any).edges = edges;
    }
  }

  function setAnnotationsMode(active: boolean) {
    state.annotationsMode.active = active;
    const projectStore = useProjectStore();

    if (state.annotationsMode.active) {
      if (projectStore.project?.annotationEntries?.length === 0) {
        state.annotationsMode.actionActive = true;
      }

      state.measureDistanceMode.enabled = false;
      state.marqueeSelectObjects.enabled = false;
      state.selectObjects.enabled = false;
      state.hideObjects.enabled = false;
      state.sliceObjectsMode.enabled = false;
    } else {
      state.annotationsMode.actionActive = false;
      state.selectObjects.enabled = true;
      state.hideObjects.enabled = true;
      state.measureDistanceMode.enabled = true;
      state.marqueeSelectObjects.enabled = true;
      state.sliceObjectsMode.enabled = true;
    }
  }

  function handleCameraControlHover(pickResult) {
    if (pickResult) {
      if (!lastHighlightedEntity || pickResult.entity.id !== lastHighlightedEntity.id) {
        if (lastHighlightedEntity) {
          lastHighlightedEntity.highlighted = false;
        }

        lastHighlightedEntity = pickResult.entity;
        if (pickResult.entity) pickResult.entity.highlighted = true;
      }
    } else {
      if (lastHighlightedEntity) {
        lastHighlightedEntity.highlighted = false;
        lastHighlightedEntity = null;
      }
    }
  }

  function handleCameraControlPicked(event) {
    const viewer = viewerStore.getHelpers().viewer;

    if (!viewer || !event.entity) return;

    const wasSelected = event.entity.selected;

    if (state.selectObjects.active) {
      if (!controlPressed.value) {
        viewerStore.propertiesInspector.metaObjects = {};
        viewerStore.propertiesInspector.loadingIds = [];
        viewer.scene.setObjectsSelected(viewer.scene.selectedObjectIds, false);
      }

      event.entity.selected = !wasSelected;

      const objectId = event.entity.id as string;

      if (event.entity.selected) {
        viewerStore.showObjectProperties(objectId);
      } else {
        delete viewerStore.propertiesInspector.metaObjects[objectId];

        viewerStore.propertiesInspector.loadingIds =
          viewerStore.propertiesInspector.loadingIds.filter((id) => id !== objectId);

        if (viewer.scene.selectedObjectIds.indexOf(objectId) === -1) {
          return;
        }
      }
    } else if (state.hideObjects.active) {
      event.entity.visible = false;
    }
  }

  function handleCameraControlPickedNothing() {
    viewerStore.clearSceneSelections();
  }

  function handleSceneInputMouseMove(coords) {
    const viewer = viewerStore.getHelpers().viewer;
    if (!viewer) return;

    const hit = viewer.scene.pick({ canvasPos: coords });

    if (!hit?.entity && lastHighlightedEntity) {
      lastHighlightedEntity.highlighted = false;
      lastHighlightedEntity = null;
      return;
    }
  }

  return {
    ...toRefs(state),
    walkModeTarget,
    walkModeHideMode,
    walkModeFlying,
    walkModeMoveMode,
    walkModeSnap,
    walkModeMoveEnabled,
    walkModeInspect,
    walkModePressedKeys,
    controlPressed,
    lastSelectedEntity,
    setSelectObjects,
    setHideObjects,
    setMeasureDistance,
    setMeasureAngle,
    setOrthoMode,
    setThreeDMode,
    setShowIfcSpaces,
    setFirstPersonModeActive,
    setFirstPersonNavigation,
    setWalkMode,
    setMarqueeSelect,
    setSliceObjects,
    setZonesFeatureMode,
    setAnnotationsMode,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useToolbarStore, import.meta.hot));
}
